import React, { useState } from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/logo.png";
import { diyApi } from "../../api/diyApi";
import type { AuthResponse } from "../../types";
import Spinner from "../../components/ui/Spinner";

const AUTH_USER_KEY = "authUser";
const TOKEN_KEY = "token";

const DiyRegisterPage: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const data = await diyApi.registerDiy({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
      });

      const businessName = `${firstName.trim()} ${lastName.trim()}'s Credit Repair`;
      const authLike: AuthResponse = {
        token: data.token,
        tenantId: String(data.tenantId),
        subdomain: "",
        operatorEmail: email.trim().toLowerCase(),
        role: "Admin",
        tenantName: businessName,
        tenantType: data.tenantType ?? "diy",
        user: {
          id: String(data.userId),
          email: email.trim().toLowerCase(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          role: "Admin",
        },
        userId: String(data.userId),
      };

      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(authLike));

      if (!data.checkoutUrl) {
        setError("Checkout could not be started. Please try again.");
        return;
      }

      window.location.assign(data.checkoutUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/80 p-8 shadow-2xl shadow-blue-900/20">
        <div className="mb-8 text-center">
          <img src={logo} alt="IntelliWarrior" className="mx-auto mb-4 h-32 w-auto object-contain" />
          <h1 className="text-xl font-semibold text-white">Create your DIY account</h1>
          <p className="mt-1 text-sm text-slate-400">Then continue to secure checkout ($49.99/mo).</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="diy-fn" className="mb-1 block text-sm text-slate-300">
                First name
              </label>
              <input
                id="diy-fn"
                value={firstName}
                onChange={(ev) => setFirstName(ev.target.value)}
                required
                className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 outline-none ring-cyan-500 placeholder:text-slate-500 focus:ring-2"
              />
            </div>
            <div>
              <label htmlFor="diy-ln" className="mb-1 block text-sm text-slate-300">
                Last name
              </label>
              <input
                id="diy-ln"
                value={lastName}
                onChange={(ev) => setLastName(ev.target.value)}
                required
                className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 outline-none ring-cyan-500 placeholder:text-slate-500 focus:ring-2"
              />
            </div>
          </div>
          <div>
            <label htmlFor="diy-email" className="mb-1 block text-sm text-slate-300">
              Email
            </label>
            <input
              id="diy-email"
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              required
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 outline-none ring-cyan-500 placeholder:text-slate-500 focus:ring-2"
            />
          </div>
          <div>
            <label htmlFor="diy-pw" className="mb-1 block text-sm text-slate-300">
              Password
            </label>
            <input
              id="diy-pw"
              type="password"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              required
              minLength={8}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 outline-none ring-cyan-500 placeholder:text-slate-500 focus:ring-2"
            />
          </div>
          <div>
            <label htmlFor="diy-pw2" className="mb-1 block text-sm text-slate-300">
              Confirm password
            </label>
            <input
              id="diy-pw2"
              type="password"
              value={confirm}
              onChange={(ev) => setConfirm(ev.target.value)}
              required
              minLength={8}
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 outline-none ring-cyan-500 placeholder:text-slate-500 focus:ring-2"
            />
          </div>

          {error ? <p className="text-sm text-red-400">{error}</p> : null}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 py-2.5 font-semibold text-white hover:from-blue-500 hover:to-cyan-500 disabled:opacity-60"
          >
            {loading ? <Spinner /> : "Create Account"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-500">
          <Link to="/diy" className="text-cyan-400 hover:underline">
            Back to DIY home
          </Link>
          <span className="mx-2 text-slate-600">·</span>
          <Link to="/login" className="text-cyan-400 hover:underline">
            Already have an account? Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default DiyRegisterPage;
